import { useMemo } from "react";
import { computeNpd, computeProcessRowCost } from "@/utils/npdCalc";
import { formatINR } from "@/utils/formatINR";
import { useAuthedImage } from "@/utils/authedImage";

const STOCK_FORM_LABELS = {
  sheet_strip: "Sheet / Strip",
  round_rod:   "Round Rod",
  pipe:        "Pipe / Tube",
  square_bar:  "Square Bar",
  hex_bar:     "Hex Bar",
};

const BASIS_LABELS = {
  process_cost: "% of Process Cost (B)",
  gross_rmc:    "% of Gross RMC",
  net_rmc:      "% of Net RMC",
  sub_total:    "% of Sub-Total",
  lump_sum:     "Lump Sum",
};

export default function NpdQuoteSheet({ npd, customerName, settings, unit }) {
  const calc = useMemo(() => computeNpd(npd || {}), [npd]);
  // R30 polish — fetch images via authenticated api (no token in URL).
  const screenshotUrl = useAuthedImage(
    npd?.drawing_screenshot_path && npd?.npd_id ? `/npd/${npd.npd_id}/screenshot` : null,
    [npd?.npd_id, npd?.drawing_screenshot_path]
  );
  const signatureUrl = useAuthedImage(
    npd?.approved_for_export && npd?.approved_by ? `/users/${npd.approved_by}/signature` : null,
    [npd?.approved_for_export, npd?.approved_by]
  );
  if (!npd) return null;

  const stockForm = npd.stock_form || "sheet_strip";
  const companyName = settings?.company_name || "Anmol Metcomp Private Limited";
  // Round 21.2 — Use unit address if non-empty; else fall back to global company_address.
  const companyAddress = (unit && unit.address) ? unit.address : (settings?.company_address || "");
  const tnc = settings?.tnc_template || "";
  const gstLine = settings?.gst_mandatory_line || "";

  const dimRows = (() => {
    if (stockForm === "sheet_strip") return [
      ["Thickness (mm)", Number(npd.thickness_mm || 0).toFixed(2)],
      ["Strip Width (mm)", Number(npd.strip_width || 0).toFixed(2)],
      ["Pitch of Feed (mm)", Number(npd.pitch || 0).toFixed(2)],
    ];
    if (stockForm === "round_rod") return [
      ["Diameter (mm)", Number(npd.rod_diameter_mm || 0).toFixed(2)],
      ["Length (mm)", Number(npd.rod_length_mm || 0).toFixed(2)],
    ];
    if (stockForm === "pipe") return [
      ["OD (mm)", Number(npd.pipe_od_mm || 0).toFixed(2)],
      ["ID (mm)", Number(npd.pipe_id_mm || 0).toFixed(2)],
      ["Length (mm)", Number(npd.pipe_length_mm || 0).toFixed(2)],
    ];
    if (stockForm === "square_bar") return [
      ["Side (mm)", Number(npd.bar_side_mm || 0).toFixed(2)],
      ["Length (mm)", Number(npd.bar_length_mm || 0).toFixed(2)],
    ];
    if (stockForm === "hex_bar") return [
      ["Across Flats (mm)", Number(npd.hex_across_flats_mm || 0).toFixed(2)],
      ["Length (mm)", Number(npd.hex_length_mm || 0).toFixed(2)],
    ];
    return [];
  })();

  const hasFAmortization = npd.amortize_tooling && (calc.tool_amortization || 0) > 0;
  const subTotalLabel = hasFAmortization ? "Sub-Total (A+B+C+D+F)" : "Sub-Total (A+B+C+D)";
  const toolingOneTime = Number(npd.tooling_cost || 0);
  const toolingLife = Number(npd.tooling_life || 0);

  // Tightened gate: the YoY block renders only when a YoY rate is actually set.
  // Volume-only entries without a discount no longer trigger it.
  const showYoy = (npd.yoy_reduction_pct || 0) > 0 && (calc.yoy_schedule || []).length > 0;

  return (
    <div className="quote-sheet text-slate-800 text-[12px] leading-snug relative">
      {!npd.approved_for_export && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-10 overflow-hidden" aria-hidden="true" data-testid="draft-watermark">
          <div className="text-[160px] font-black text-red-500 opacity-[0.12] -rotate-45 select-none whitespace-nowrap tracking-widest">
            DRAFT
          </div>
        </div>
      )}
      <div className="grid grid-cols-[80px_1fr] border border-slate-700 mb-2">
        <div className="bg-white flex items-center justify-center p-1 row-span-2">
          <img src="/aampl-logo.png" alt="AAMPL" className="max-w-full max-h-[55px]" />
        </div>
        <div className="bg-orange-500 text-white font-bold text-center py-1.5 text-base">{companyName}</div>
        <div className="text-center italic text-slate-500 text-[11px] py-1 border-t border-slate-700">
          {companyAddress || "—"}
        </div>
      </div>

      <div className="bg-amber-200 text-slate-800 font-bold text-center py-1.5 border border-slate-700 text-sm mb-2">
        COMPONENT COST ESTIMATE
      </div>

      <Table4Col rows={[
        ["Quotation No.", npd.npd_number || "", "Date", new Date().toISOString().slice(0, 10)],
        ["Revision", `R${npd.current_round || 0}`, "Validity", "30 Days"],
        ["Customer", customerName || "—", "End Customer", npd.end_customer || "—"],
      ]} />

      <SectionBar letter="" title="PART DETAILS" />
      <Table4Col rows={[
        ["Part Name / No.", `${npd.part_name || ""} / ${npd.part_number || ""}`, "Model", npd.model_name || "—"],
        ["Drawing No.", npd.drawing_number || "—", "Application", npd.application || "—"],
        // R32 — Customer-facing MOQ line (only when MOQ is set on the NPD)
        ...(npd.moq_block?.moq_batch_pcs ? [[
          "Min Order Qty (pcs)",
          `${Math.round(npd.moq_block.moq_batch_pcs).toLocaleString("en-IN")} — based on supplier batch of ${Math.round(npd.moq_block.moq_kg || 0)} kg${npd.material_grade ? ` of ${npd.material_grade}` : ""}`,
          "",
          "",
        ]] : []),
        ["Status", (npd.status || "draft").toUpperCase(), "", ""],
      ]} />

      <SectionBar letter="A" title="RAW MATERIAL" />
      <div className="flex gap-3 items-stretch">
        <div className="flex-1 min-w-0">
          <Table2Col rows={[
            ["RM Grade", npd.material_grade || ""],
            ["Stock Form", STOCK_FORM_LABELS[stockForm] || stockForm],
            ...dimRows,
            ["Gross Weight (kg)", (Number(npd.gross_weight || 0) / Math.max(1, Number(npd.cavity_count) || 1)).toFixed(4)],
            ["Net Weight (kg)", Number(npd.net_weight || 0).toFixed(4)],
            ["RM Price (₹/kg)", formatINR(npd.rm_price_per_kg)],
            ["Scrap Price (₹/kg)", formatINR(npd.scrap_price_per_kg)],
            ["Gross RM Cost (₹)", formatINR(calc.gross_rmc)],
            ["Scrap Recovery (₹)", formatINR(calc.scrap_recovery)],
          ]} />
        </div>
        {npd.drawing_screenshot_path && (
          <div className="w-[280px] flex-shrink-0 border border-slate-300 bg-white p-2 flex flex-col">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
              Drawing
            </div>
            <div className="flex-1 flex items-center justify-center">
              {screenshotUrl && (
                <img
                  src={screenshotUrl}
                  alt="Drawing preview"
                  className="max-w-full max-h-[320px] object-contain"
                />
              )}
            </div>
            {npd.drawing_number && (
              <div className="text-[10px] text-slate-500 text-center mt-1 truncate">
                Dwg: {npd.drawing_number}
              </div>
            )}
          </div>
        )}
      </div>
      <SectionTotal letter="A" label="Net Raw Material Cost (A)" value={calc.net_rmc} />

      {/* R31 — Sourcing note (monthly RM + low-volume flag). Server already
          stamps monthly_rm_kg / low_volume_flag / monthly_rm_threshold_kg /
          moq_block on the npd doc via serialize_npd, so we read them directly
          off the prop — computeNpd() intentionally only handles cost calc. */}
      {Number(npd.monthly_rm_kg || 0) > 0 && (
        <div className="mt-2 text-xs border-l-4 border-slate-300 bg-slate-50 px-3 py-2" data-testid="quote-sourcing-block">
          <div className="flex justify-between">
            <span className="text-slate-600">
              Peak-Month RM Requirement{npd.peak_year_label ? ` (${npd.peak_year_label})` : ""}
            </span>
            <span className="font-mono">{Number(npd.monthly_rm_kg).toFixed(2)} kg/month</span>
          </div>
          {Number(npd.cavity_count) > 1 && (
            <div className="text-slate-500 mt-1" data-testid="quote-multi-cavity">
              Multi-Cavity Tool: {Number(npd.cavity_count)} Cavities
            </div>
          )}
          {npd.low_volume_flag && (
            <div className="text-amber-800 mt-1">
              ⚠ Below material threshold ({Number(npd.monthly_rm_threshold_kg || 2000).toFixed(0)} kg/month).
              {npd.moq_block && (
                <span className="ml-1">
                  MOQ batch{" "}
                  {Math.round(npd.moq_block.moq_batch_pcs).toLocaleString("en-IN")} pcs
                  {npd.moq_block.months_of_cover != null && (
                    <> ({Number(npd.moq_block.months_of_cover).toFixed(1)} months cover, {npd.moq_block.status})</>
                  )}.
                  {/* R45 — supplier name intentionally hidden from customer-facing quote preview.
                      moq_confirmed_with is admin-only and lives in the form / costing dashboard. */}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <SectionBar letter="B" title="PROCESS COST" />
      <ItemTable
        headers={["#", "Process", "₹/pc"]}
        rows={(npd.processes || []).map((p, i) => [i + 1, p.name || "", formatINR(computeProcessRowCost(p, npd))])}
        widths={["w-10", "", "w-28 text-right"]}
      />
      <SectionTotal letter="B" label="Total Process Cost (B)" value={calc.process_cost_total} />

      <SectionBar letter="C" title="BOUGHT OUT PARTS" />
      <ItemTable
        headers={["#", "Item", "Qty", "₹/unit", "Total ₹"]}
        rows={(npd.bop_items || []).map((b, i) => {
          const qty = Number(b.qty || 0);
          const up = Number(b.unit_price || 0);
          return [i + 1, b.name || "", qty.toFixed(2), formatINR(up), formatINR(qty * up)];
        })}
        widths={["w-10", "", "w-20 text-right", "w-24 text-right", "w-28 text-right"]}
      />
      <SectionTotal letter="C" label="Total BOP Cost (C)" value={calc.bop_cost_total} />

      <SectionBar letter="D" title="TREATMENT & MAINTENANCE" />
      <DTable npd={npd} calc={calc} />
      <SectionTotal letter="D" label="Total Treatment & Maintenance (D)" value={calc.treatment_total} />

      {hasFAmortization && (
        <>
          <SectionBar letter="F" title="TOOL AMORTIZATION" />
          <Table2Col rows={[
            ["Tooling Cost ÷ Tooling Life", formatINR(calc.tool_amortization)],
            ["Calculation",
              `₹${Number(calc.tooling_cost_one_time || 0).toLocaleString("en-IN")} ÷ ${Math.round(calc.tooling_life_cycles || 0).toLocaleString("en-IN")} cycles`],
          ]} />
          <SectionTotal letter="F" label="Tool Amortization (F)" value={calc.tool_amortization} />
        </>
      )}

      <div className="grid grid-cols-[40px_1fr_120px] bg-amber-200 border border-slate-700 my-2">
        <div></div>
        <div className="font-bold px-2 py-1.5">{subTotalLabel}</div>
        <div className="font-bold text-right px-2 py-1.5">{formatINR(calc.sub_total)}</div>
      </div>

      <SectionBar letter="E" title="OVERHEADS & MARGIN" />
      <ItemTable
        headers={["#", "Item", "Value", "Basis", "₹/pc", "Remarks"]}
        rows={(calc.overheads_calculated || []).map((oh, i) => {
          const val = Number(oh.value || 0);
          return [
            i + 1,
            oh.label || "",
            oh.basis === "lump_sum" ? `₹${val.toFixed(2)}` : `${(val * 100).toFixed(2)}%`,
            BASIS_LABELS[oh.basis] || "",
            formatINR(oh.calculated || 0),
            oh.remarks || "",
          ];
        })}
        widths={["w-10", "", "w-24 text-right", "w-40", "w-28 text-right", "w-40 italic text-slate-600"]}
      />
      <SectionTotal letter="E" label="Total Overheads & Margin (E)" value={calc.overheads_and_margin} />

      {/* R46 — Section F: shown exactly like the software's live panel — whenever the
          adjustment total is non-zero (so the export matches the screen word-for-word). */}
      {(calc.custom_adjustments_calculated || []).length > 0 && (calc.custom_adjustments_total || 0) !== 0 && (
        <>
          <SectionBar letter="F" title="CUSTOM ADJUSTMENTS" />
          <div className="border-x border-b border-slate-700">
            {(calc.custom_adjustments_calculated || []).map((adj, i) => {
              const val = Number(adj.value || 0);
              const amt = Number(adj.amount || 0);
              const basisText = adj.mode === "pct"
                ? `${val >= 0 ? "+" : ""}${val.toFixed(2)}%`
                : `${val >= 0 ? "+" : ""}₹${val.toFixed(2)}/pc lumpsum`;
              return (
                <div key={`adj-${i}`} className="grid grid-cols-[40px_1fr_140px] text-sm border-t border-slate-200 first:border-t-0">
                  <div className="px-2 py-1.5 text-center text-slate-500">{i + 1}</div>
                  <div className="px-2 py-1.5">
                    <span className="text-slate-800">{adj.label || "Adjustment"}</span>
                    <span className="text-xs text-slate-400 ml-2">({basisText})</span>
                  </div>
                  <div className="px-2 py-1.5 text-right font-mono">
                    {amt >= 0 ? "+ " : "− "}{formatINR(Math.abs(amt))}
                  </div>
                </div>
              );
            })}
          </div>
          <SectionTotal letter="F" label="Total Section F (Adjustments)" value={calc.custom_adjustments_total || 0} />
        </>
      )}

      <div className="grid grid-cols-[1fr_140px] bg-orange-500 text-white border border-slate-700 my-2">
        <div className="font-bold px-2 py-2 text-base">
          GRAND TOTAL / SELLING PRICE ({(calc.custom_adjustments_calculated || []).length > 0 && (calc.custom_adjustments_total || 0) !== 0 ? "A+B+C+D+E+F" : "A+B+C+D+E"})
        </div>
        <div className="font-bold text-right px-2 py-2 text-base">{formatINR(calc.grand_total)}</div>
      </div>

      {(() => {
        // One-time charges block — tooling (unless amortized) + every "Other One-time
        // Development Cost" line (e.g. Testing). Display only; totals come from calc.
        const showTooling = toolingOneTime > 0 && !npd.amortize_tooling;
        const devLines = (calc.other_dev_costs || []).filter((d) => Number(d.amount) > 0);
        const hasOneTime = showTooling || devLines.length > 0;
        const oneTimeTotal =
          (showTooling ? toolingOneTime : 0) + devLines.reduce((s, d) => s + Number(d.amount || 0), 0);
        const lineCount = (showTooling ? 1 : 0) + devLines.length;
        return (
          <>
            {hasOneTime && (
              <div className="border border-slate-700 mt-3">
                <div className="bg-amber-100 text-amber-900 font-bold px-2 py-1.5 border-b border-slate-700">
                  ONE-TIME CHARGES — charged separately, not included in ₹/pc above
                </div>
                {showTooling && (
                  <div className="grid grid-cols-[1fr_140px] border-b border-slate-700">
                    <div className="px-2 py-1.5">
                      Tooling{toolingLife > 0 ? ` (life ${Math.round(toolingLife).toLocaleString("en-IN")} cycles)` : ""}
                    </div>
                    <div className="text-right px-2 py-1.5">{formatINR(toolingOneTime)}</div>
                  </div>
                )}
                {devLines.map((d, i) => (
                  <div key={i} className="grid grid-cols-[1fr_140px] border-b border-slate-700">
                    <div className="px-2 py-1.5">
                      {d.category || "Development"}{d.remarks ? ` — ${d.remarks}` : ""}
                    </div>
                    <div className="text-right px-2 py-1.5">{formatINR(d.amount)}</div>
                  </div>
                ))}
                {lineCount > 1 && (
                  <div className="grid grid-cols-[1fr_140px] bg-amber-200 text-amber-900 font-bold">
                    <div className="px-2 py-1.5">TOTAL ONE-TIME CHARGE</div>
                    <div className="text-right px-2 py-1.5">{formatINR(oneTimeTotal)}</div>
                  </div>
                )}
              </div>
            )}
            {npd.amortize_tooling && (calc.tool_amortization || 0) > 0 && (
              <div className="text-[11px] italic text-slate-500 px-1 py-1">
                Tooling cost (₹{Number(toolingOneTime).toLocaleString("en-IN")} over {Math.round(toolingLife).toLocaleString("en-IN")} cycles) is amortized into ₹/pc above. No separate tooling charge.
              </div>
            )}
          </>
        );
      })()}

      {showYoy && (
        <div className="mt-3">
          <div className="bg-orange-500 text-white font-bold px-2 py-1.5 border border-slate-700">
            VOLUMES & YEAR-OVER-YEAR SCHEDULE
          </div>
          <div className="italic text-slate-500 text-[11px] py-1 px-1">
            YoY Reduction: {Number(npd.yoy_reduction_pct || 0).toFixed(1)}% per year compound · Project Life: {npd.project_life_years || 1} years
          </div>
          <table className="w-full text-[11px] border-collapse border border-slate-700">
            <thead className="bg-amber-200 text-slate-700 font-bold">
              <tr>
                <th className="border border-slate-700 p-1 w-12 text-center">Year</th>
                <th className="border border-slate-700 p-1 w-16 text-center">Calendar</th>
                <th className="border border-slate-700 p-1 w-16 text-center">Reduction %</th>
                <th className="border border-slate-700 p-1 text-right">Price/pc (₹)</th>
                <th className="border border-slate-700 p-1 text-right">Annual Vol (pcs)</th>
                <th className="border border-slate-700 p-1 text-right">Monthly Vol</th>
                <th className="border border-slate-700 p-1 text-right">Annual Value (₹)</th>
              </tr>
            </thead>
            <tbody>
              {(calc.yoy_schedule || []).map((s) => (
                <tr key={s.year_number}>
                  <td className="border border-slate-700 p-1 text-center">Y{s.year_number}</td>
                  <td className="border border-slate-700 p-1 text-center">{s.year_label || ""}</td>
                  <td className="border border-slate-700 p-1 text-center font-mono">{s.year_number === 1 ? "base" : `${Number(s.reduction_pct || 0).toFixed(2)}%`}</td>
                  <td className="border border-slate-700 p-1 text-right font-mono">{formatINR(s.price_per_pc)}</td>
                  <td className="border border-slate-700 p-1 text-right font-mono">{Math.round(s.annual_volume_pcs).toLocaleString("en-IN")}</td>
                  <td className="border border-slate-700 p-1 text-right font-mono">{Math.round(s.monthly_volume_pcs || 0).toLocaleString("en-IN")}</td>
                  <td className="border border-slate-700 p-1 text-right font-mono">{formatINR(s.annual_value)}</td>
                </tr>
              ))}
              <tr className="bg-orange-500 text-white font-bold">
                <td colSpan={6} className="border border-slate-700 p-1.5 text-right">TOTAL PROJECT VALUE</td>
                <td className="border border-slate-700 p-1.5 text-right">{formatINR(calc.total_project_value)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {tnc && (
        <div className="mt-4">
          <div className="bg-amber-200 text-slate-700 font-bold px-2 py-1 border border-slate-700">
            TERMS & CONDITIONS
          </div>
          <div className="p-2 space-y-1 text-[11px]">
            {tnc.split("\n").map((line, i) => {
              const cleaned = (line || "").replace(/\*\*/g, "").trim();
              return cleaned ? <div key={i}>{cleaned}</div> : null;
            })}
            {gstLine && (
              <div className="font-bold italic text-orange-600 mt-2">* {gstLine}</div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 mt-6">
        <div>
          {/* R30 — embed approver signature image when NPD is approved + approver has signature */}
          {npd.approved_for_export && npd.approved_by && signatureUrl && (
            <div className="h-12 mb-1">
              <img
                src={signatureUrl}
                alt=""
                className="h-12 object-contain"
                data-testid="approver-signature"
              />
            </div>
          )}
          <div className="font-bold border-b border-slate-700 pb-1">For {companyName}</div>
          <div className="italic text-slate-500 text-[11px] pt-1">Authorised Signatory</div>
          {npd.approved_for_export && npd.approved_by_name && (
            <div className="text-[12px] font-semibold text-slate-900 pt-1" data-testid="approver-name">
              {npd.approved_by_name}
              {npd.approved_at && (
                <span className="italic text-slate-500 font-normal text-[11px] ml-2">
                  Approved: {String(npd.approved_at).slice(0, 10)}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="pl-6">
          <div className="font-bold border-b border-slate-700 pb-6 mt-12">Customer Acceptance</div>
          <div className="italic text-slate-500 text-[11px] pt-1">Name &amp; Designation</div>
        </div>
      </div>
    </div>
  );
}

function SectionBar({ letter, title }) {
  return (
    <div className="bg-amber-100 border border-slate-700 grid grid-cols-[40px_1fr] mt-2">
      <div className="font-bold text-center py-1 border-r border-slate-700">{letter}</div>
      <div className="font-bold py-1 px-2">{title}</div>
    </div>
  );
}

function SectionTotal({ letter, label, value }) {
  return (
    <div className="grid grid-cols-[40px_1fr_120px] bg-amber-100 border border-t-0 border-slate-700">
      <div className="font-bold text-center py-1.5 border-r border-slate-700">{letter}</div>
      <div className="font-bold px-2 py-1.5">{label}</div>
      <div className="font-bold text-right px-2 py-1.5 font-mono">{formatINR(value)}</div>
    </div>
  );
}

function Table2Col({ rows }) {
  return (
    <table className="w-full border-collapse border border-slate-700">
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td className="border border-slate-700 p-1 px-2 w-[60mm] font-semibold">{r[0]}</td>
            <td className="border border-slate-700 p-1 px-2">{r[1]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Table4Col({ rows }) {
  return (
    <table className="w-full border-collapse border border-slate-700">
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td className="border border-slate-700 p-1 px-2 w-[28mm] font-semibold">{r[0]}</td>
            <td className="border border-slate-700 p-1 px-2 w-1/3">{r[1]}</td>
            <td className="border border-slate-700 p-1 px-2 w-[28mm] font-semibold">{r[2]}</td>
            <td className="border border-slate-700 p-1 px-2 w-1/3">{r[3]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ItemTable({ headers, rows, widths }) {
  return (
    <table className="w-full border-collapse border border-slate-700">
      <thead className="bg-amber-200">
        <tr>
          {headers.map((h, i) => (
            <th key={i} className={`border border-slate-700 p-1 font-bold ${widths[i] || ""}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={headers.length} className="border border-slate-700 p-2 text-center text-slate-400 italic">No items</td></tr>
        ) : rows.map((r, i) => (
          <tr key={i}>
            {r.map((cell, j) => (
              <td key={j} className={`border border-slate-700 p-1 px-2 ${widths[j]?.includes("right") ? "text-right font-mono" : ""}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DTable({ npd, calc }) {
  const surf = npd.surface_treatment || { mode: "per_piece", rate: 0, notes: "" };
  const heat = npd.heat_treatment || { mode: "per_piece", rate: 0, notes: "" };
  const metaStr = (t) => {
    const m = [];
    // Round 19.9 — rate breakdown hidden from customer-facing preview. Only notes shown.
    if (t.notes) m.push(t.notes);
    return m.join(" · ");
  };
  const tmPct = (() => {
    const raw = Number(npd.tool_maintenance_pct);
    if (!raw && raw !== 0) return 10;
    if (raw < 1.5) return raw * 100;
    return raw;
  })();
  const rows = [
    ["Surface Treatment", calc.surface_cost, metaStr(surf)],
    ["Heat Treatment", calc.heat_cost, metaStr(heat)],
    ["Tool Insert & Maintenance", calc.tool_maintenance,
     npd.tool_maintenance_overridden ? "Fixed (overridden)" : `@ ${tmPct.toFixed(1)}% of Process Cost`],
  ];
  return (
    <table className="w-full border-collapse border border-slate-700">
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td className="border border-slate-700 p-1 px-2 w-[60mm] font-semibold">{r[0]}</td>
            <td className="border border-slate-700 p-1 px-2 w-28 text-right font-mono">{formatINR(r[1])}</td>
            <td className="border border-slate-700 p-1 px-2 italic text-slate-500 text-[11px]">{r[2]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
